import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../hooks/useTranslation";
import { fetchDishByIdThunk } from "../store/slices/dishesReducer";
import {
  updateConnectionPosition,
  updateMealAttributes,
  updateDishDescription,
  updateExtraIngredients,
  updateDishPublicPage,
  acknowledgeDishProductSyncNotice,
} from "../api/api_dishes";
import { apiClient } from "../api/api_base";
import {fetchParameterAveragesByCategory} from "../api/api_categories";
import TableRowComponent from "../components/TableRowComponent";
import TableRowComponentNew from "../components/TableRowComponentNew";
import DishSingleViewSummeryCard from "../components/Cards/DishSingleViewSummeryCard";
import DishEnergyDistributionChart from "../components/DishEnergyDistributionChart";
import CompositionPercentagesCard from "../components/CompositionPercentagesCard";
import ParametersGroups from "../components/ParametersGroups.jsx";
import DishQrModal from "../components/DishQrModal";
import { getDishAverages } from "../utils/sustainabilityAverages";
import { calculateEnergyFromMacros } from "../utils/energyCalculation";
import { printIngredientsSheet } from "../utils/printIngredientsSheet";
import { DishFileToPrint, DishRecipeFileToPrint } from "../reports";
import { setCategoryAverages } from "../store/slices/categoriesReducer";
import JourneyBreadcrumb from "../components/navigation/JourneyBreadcrumb";
import WorkflowDrawer from "../components/navigation/WorkflowDrawer";
import SideDrawer from "../components/navigation/SideDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { PiPrinterLight } from "react-icons/pi";
import { PiBowlFoodLight } from "react-icons/pi";
import { CiViewList } from "react-icons/ci";
import { FiExternalLink } from "react-icons/fi";
import { UserCheck } from "lucide-react";



const ItemPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    currentDish: item,
    status,
    error,
  } = useSelector((state) => state.dishes);
  const authUser = useSelector((state) => state.affiliate?.auth) || null;
  const [adding, setAdding] = useState(false);
  const [showFilter] = useState(false);
  const [filterCriteria] = useState({
    minSustainability: 0,
    maxSustainability: 100,
    origin: "",
    ingredientType: "",
  });
  const [draggingId, setDraggingId] = useState(null);
  const [components, setComponents] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [showNutrientDrawer, setShowNutrientDrawer] = useState(false);
  const [showCompositionsDrawer, setShowCompositionsDrawer] = useState(false);
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showIngredientsExpanded, setShowIngredientsExpanded] = useState(false);
  const [copiedIngredients, setCopiedIngredients] = useState(false);
  const [mealTypes, setMealTypes] = useState([]);
  const [dayTags, setDayTags] = useState([]);
  const [isDagensMeny, setIsDagensMeny] = useState(false);
  const [updatingMealAttributes, setUpdatingMealAttributes] = useState(false);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [newExtraIngredient, setNewExtraIngredient] = useState("");
  const [updatingExtraIngredients, setUpdatingExtraIngredients] =
    useState(false);
  const [showExtraIngredientsExpanded, setShowExtraIngredientsExpanded] =
    useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isPublicPage, setIsPublicPage] = useState(false);
  const [savingPublicPage, setSavingPublicPage] = useState(false);
  const [dismissedProductSyncNotice, setDismissedProductSyncNotice] = useState(false);
  const [closingProductSyncNotice, setClosingProductSyncNotice] = useState(false);
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: "",
    description: "",
    mode: "alert",
  });

  useEffect(() => {
    if (showNutrientDrawer || showCompositionsDrawer) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [showNutrientDrawer, showCompositionsDrawer]);


  // const getCountryCodeFromFlag = (url) => {
  //   if (!url || typeof url !== "string") return "";
  //   try {
  //     const last = url.split("/").pop() || "";
  //     const base = last.split(".")[0] || "";
  //     return (base || "").toUpperCase();
  //   } catch (e) {
  //     return "";
  //   }
  // };

  useEffect(() => {
    if (item?.components) {
      setComponents(item.components);
    } else {
      setComponents([]);
    }
  }, [item]);

  useEffect(() => {
    if (item) {
      setMealTypes(item.meal_types || []);
      setDayTags(item.day_tags || []);
      setIsDagensMeny(item.is_dagens_meny || false);
      setDescription(item.description || "");
    }
  }, [item]);

  useEffect(() => {
    setDismissedProductSyncNotice(false);
    setClosingProductSyncNotice(false);
  }, [id]);

  const handleCloseProductSyncNotice = async () => {
    if (!id || closingProductSyncNotice) return;
    setClosingProductSyncNotice(true);
    try {
      await acknowledgeDishProductSyncNotice(id);
      setDismissedProductSyncNotice(true);
    } catch (err) {
      console.error("Failed to acknowledge dish product sync notice:", err);
      setDismissedProductSyncNotice(true);
    } finally {
      setClosingProductSyncNotice(false);
    }
  };

  const showErrorDialog = (description, title = t("Något gick fel")) => {
    setAlertDialog({
      open: true,
      title,
      description: String(description || t("Ett oväntat fel inträffade.")),
      mode: "alert",
    });
  };

  const showConfirmDialog = (title, description) =>
    new Promise((resolve) => {
      setAlertDialog({
        open: true,
        title,
        description,
        mode: "confirm",
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const closeAlertDialog = () => {
    setAlertDialog((prev) => ({
      ...prev,
      open: false,
      onConfirm: undefined,
      onCancel: undefined,
    }));
  };

  const handlePrintIngredientsSheet = () =>
    printIngredientsSheet({
      dishName: (item?.name || t("Recept")).toString(),
      dishImage: item?.image || "",
      affiliateName: (item?.creator?.name || authUser?.name || "").toString(),
      affiliateImage: authUser?.image || "",
      ingredientsText: (originalContentsDisplay || "").toString(),
      labels: {
        title: t("Innehållsförteckning"),
        generatedAt: t("Uppdaterad"),
        openWindowError: t("Kunde inte öppna utskriftsfönster"),
      },
    });

  const originalContentsDisplay = useMemo(() => {
    if (!components || components.length === 0) {
      return item?.original_contents || "";
    }

    const formatPercentage = (value) => {
      const num = parseFloat(value);
      if (!Number.isFinite(num)) return null;
      const rounded = parseFloat(num.toFixed(1));
      if (!Number.isFinite(rounded)) return null;
      return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
    };

    const stripPrefix = (text) => {
      if (!text || typeof text !== "string") return "";
      return text
        .replace(/^\s*Ingredienser\s*:\s*/iu, "")
        .replace(/\s+/g, " ")
        .trim();
    };

    // Sort components by percentage in descending order
    const sortedComponents = [...components].sort((a, b) => {
      const getPercentage = (comp) => {
        const pct = comp.percentage ?? comp.weight_in_percentage ?? comp.inserted_percentage;
        const num = parseFloat(pct);
        return Number.isFinite(num) ? num : 0;
      };
      return getPercentage(b) - getPercentage(a);
    });

    const parts = sortedComponents
      .map((component) => {
        const pctStr = formatPercentage(
          component.percentage ?? component.weight_in_percentage ?? component.inserted_percentage
        );

        const applyPercentage = (label) => {
          if (!label) return "";
          return pctStr ? `${label} (${pctStr})` : label;
        };

        const baseName =
          component.name ||
          component.product_name ||
          component.dish_name ||
          component.title ||
          component.label ||
          "";

        if (
          component.type === "ingredient" ||
          component.ingredient_id ||
          component.sourceType === "ingredient"
        ) {
          return applyPercentage(baseName);
        }

        if (
          component.type === "product" ||
          component.product_id ||
          component.sourceType === "product"
        ) {
          const productName = applyPercentage(baseName);
          const productContents = stripPrefix(
            component.original_contents ||
              component.product_original_contents ||
              component.contents ||
              ""
          );
          if (productContents) {
            return productName ? `${productName}: ${productContents}` : productContents;
          }
          return productName;
        }

        if (
          component.type === "recipe" ||
          component.child_dish_id ||
          component.sourceType === "recipe"
        ) {
          const childContents = stripPrefix(
            component.original_contents ||
              component.child_original_contents ||
              component.child_contents ||
              ""
          );
          if (childContents) {
            return childContents;
          }
          return applyPercentage(baseName);
        }

        return applyPercentage(baseName);
      })
      .filter((part) => part && part.length > 0);

    if (!parts.length) {
      return item?.original_contents || "";
    }

    return `Ingredienser: ${parts.join(", ")}`;
  }, [components, item?.original_contents]);

  const handleDragStart = (e, component) => {
    setDraggingId(component.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, overComponent) => {
    e.preventDefault();
    if (!components || components.length === 0) return;
    if (draggingId == null || draggingId === overComponent.id) return;

    const current = [...components];
    const fromIndex = current.findIndex((c) => c.id === draggingId);
    const toIndex = current.findIndex((c) => c.id === overComponent.id);
    if (fromIndex === -1 || toIndex === -1) return;

    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);

    setComponents(current);
    setOrderDirty(true);
  };

  const handleSaveOrder = async () => {
    if (!orderDirty || savingOrder) return;
    setSavingOrder(true);
    try {
      // Persist positions as 1-based sequence
      const updates = components.map((c, idx) =>
        updateConnectionPosition(c.connection_id || c.id, idx + 1)
      );
      await Promise.all(updates);
      setOrderDirty(false);
      // Refresh dish from backend to reflect persisted order
      dispatch(fetchDishByIdThunk(id));
    } catch (err) {
      console.error("Failed to save order:", err);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleMealAttributesUpdate = async () => {
    if (updatingMealAttributes) return;
    setUpdatingMealAttributes(true);
    try {
      await updateMealAttributes(id, {
        meal_types: mealTypes,
        day_tags: dayTags,
        is_dagens_meny: isDagensMeny,
      });
      // Refresh dish from backend to reflect updated attributes
      dispatch(fetchDishByIdThunk(id));
    } catch (err) {
      console.error("Failed to update meal attributes:", err);
    } finally {
      setUpdatingMealAttributes(false);
    }
  };

  const handleSaveDescription = async () => {
    if (savingDescription) return;
    setSavingDescription(true);
    try {
      await updateDishDescription(id, description);
      dispatch(fetchDishByIdThunk(id));
    } catch (error) {
      console.error("Failed to update description:", error);
    } finally {
      setSavingDescription(false);
    }
  };

  const handleUpdateExtraIngredients = async (extraIngredients) => {
    if (updatingExtraIngredients) return;
    setUpdatingExtraIngredients(true);
    try {
      await updateExtraIngredients(id, extraIngredients);
      // Refresh dish from backend to reflect updated extra ingredients
      dispatch(fetchDishByIdThunk(id));
    } catch (err) {
      console.error("Failed to update extra ingredients:", err);
    } finally {
      setUpdatingExtraIngredients(false);
    }
  };

  const handleAddExtraIngredient = () => {
    if (!newExtraIngredient.trim()) return;

    const currentIngredients = item.extra_ingredients || [];
    const updatedIngredients = [
      ...currentIngredients,
      newExtraIngredient.trim(),
    ];

    setNewExtraIngredient("");
    handleUpdateExtraIngredients(updatedIngredients);
  };

  const handlePublishDish = async () => {
    if (!item?.gtin) {
      showErrorDialog(t("Dish must have a GTIN to be published"), t("Saknar GTIN"));
      return;
    }

    const confirmed = await showConfirmDialog(
      t("Publicera som produkt"),
      t("Är du säker på att du vill publicera denna rätt som produkt?")
    );
    if (!confirmed) {
      return;
    }

    console.log('Publishing dish:', { dish_id: id, gtin: item.gtin });
    setPublishing(true);
    try {
      const response = await apiClient.post('/dishes/publish', {
        dish_id: id,
        gtin: item.gtin
      });

      console.log('Publish response:', response);
      setIsPublished(true);
    } catch (error) {
      console.error('Error publishing dish:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      showErrorDialog(t('Failed to publish dish: ') + errorMessage, t("Publicering misslyckades"));
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishDish = async () => {
    const confirmed = await showConfirmDialog(
      t("Avpublicera produkt"),
      t("Are you sure you want to unpublish this dish? It will no longer appear in product search.")
    );
    if (!confirmed) {
      return;
    }

    console.log('Unpublishing dish:', { dish_id: id });
    setPublishing(true);
    try {
      const response = await apiClient.post('/dishes/unpublish', {
        dish_id: id
      });

      console.log('Unpublish response:', response);
      setIsPublished(false);
    } catch (error) {
      console.error('Error unpublishing dish:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      showErrorDialog(t('Failed to unpublish dish: ') + errorMessage, t("Avpublicering misslyckades"));
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenConsupediaProduct = () => {
    const gtin = item?.gtin ? String(item.gtin).trim() : "";
    if (!gtin) return;

    const backendProductBase = process.env.REACT_APP_BACKEND_PRODUCT_URL;
    if (!backendProductBase) return;

    const productUrl = `${backendProductBase.replace(/\/$/, "")}/products/${encodeURIComponent(gtin)}`;
    window.open(productUrl, "_blank", "noopener,noreferrer");
  };


  useEffect(() => {
    if (id) {
      dispatch(fetchDishByIdThunk(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const flag = item?.is_public_page;
    if (typeof flag === "boolean") {
      setIsPublicPage(flag);
    }
  }, [item?.is_public_page]);

  // Check if dish is already published
  useEffect(() => {
    if (item?.id) {
      apiClient.get(`/dishes/${item.id}/published-status`)
        .then(response => {
          setIsPublished(response.data.is_published || false);
          if (typeof response.data.is_public_page === "boolean") {
            setIsPublicPage(response.data.is_public_page);
          }
        })
        .catch(error => {
          console.error('Error checking publication status:', error);
        });
    }
  }, [item?.id]);

  const handlePublicPageToggle = async (enabled) => {
    if (!item?.id) return;
    setSavingPublicPage(true);
    try {
      await updateDishPublicPage(item.id, enabled);
      setIsPublicPage(enabled);
      dispatch(fetchDishByIdThunk(id));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.message || "Unknown error";
      showErrorDialog(t("Kunde inte spara") + ": " + errorMessage, t("Sparning misslyckades"));
    } finally {
      setSavingPublicPage(false);
    }
  };

  // Nutrients now come embedded with dish payload from backend

  const getAverageScore = (dish) => {
    const averages = getDishAverages(dish);
    const totalAverage =
      (averages.environment_average +
        averages.health_average +
        averages.social_average) /
      3;
    return Math.round(totalAverage);
  };

  const filteredComponents =
    (components || []).filter((component) => {
      const avgScore = getAverageScore({ ingredients: [component] });
      return (
        avgScore >= filterCriteria.minSustainability &&
        avgScore <= filterCriteria.maxSustainability &&
        (filterCriteria.origin === "" ||
          (component.origin || "")
            .toLowerCase()
            .includes(filterCriteria.origin.toLowerCase())) &&
        (filterCriteria.ingredientType === "" ||
          (component.name || "")
            .toLowerCase()
            .includes(filterCriteria.ingredientType.toLowerCase()))
      );
    }) || [];

  useEffect(() => {
    if (item?.affiliate_category_id && item?.affiliate_id) {
      fetchParameterAveragesByCategory(item.affiliate_category_id, item.affiliate_id).then((response) => {
        if (response.data.success) {
          dispatch(setCategoryAverages(response.data));
        }
        else {
          console.error("Failed to fetch category averages:", response.data);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.affiliate_category_id, item?.affiliate_id]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-600 text-lg font-medium">
            {t('Laddar recept...')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/assets/icons/Radera.png"
                alt="Error"
                className="w-8 h-8 text-red-600"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('Fel vid laddning av recept')}
            </h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/dashboard"
              className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('Tillbaka till Kategorier')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/assets/icons/Innehall.png"
                alt="Not found"
                className="w-8 h-8 text-gray-400"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('Recept hittades inte')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('Det begärda receptet kunde inte hittas.')}
            </p>
            <Link
              to="/dashboard"
              className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('Tillbaka till Kategorier')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t('Översikt'), to: '/' },
            item?.category?.id
              ? { label: t(item.category.name || t('Kategori')), to: `/categories/${item.category.id}` }
              : { label: t(item?.category?.name || t('Kategori')) },
            { label: item?.name || t('Recept') },
          ]}
          className="mb-6"
        />
        {item?.has_product_sync_notice && !dismissedProductSyncNotice && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3">
            <p className="text-sm text-amber-900 m-0">
              {t("Kopplade GTIN-produkter har uppdaterats. Rätten visar nu senaste data.")}
            </p>
            <button
              type="button"
              onClick={handleCloseProductSyncNotice}
              disabled={closingProductSyncNotice}
              className="text-amber-800 hover:text-amber-950 text-sm font-semibold disabled:opacity-50"
            >
              {t("Stäng")}
            </button>
          </div>
        )}
        {/* Header */}
        <div className="mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
          <div className="flex flex-col">
            <div>
              <div className="flex items-center justify-between mb-2 mobile:mb-3 tablet:mb-3 laptop:mb-3 desktop:mb-3 w-full">
                <Link
                  to="/menyer"
                  className="inline-flex items-center text-secondary-green hover:text-secondary-green/80 font-semibold transition-colors duration-200"
                >
                  <img
                    src="/assets/icons/arrow_right.png"
                    alt="Visa alla recept"
                    className="w-5 h-5 mr-2 transform rotate-180"
                  />
                  {t('Visa alla recept')}
                </Link>
                <button
                  type="button"
                  onClick={() => setWorkflowOpen(true)}
                  className="hidden mobile:inline-flex tablet:hidden items-center gap-2 rounded-lg border border-secondary-green/40 bg-secondary-green/10 px-3 py-2 text-xs font-semibold text-black transition hover:bg-secondary-green/20"
                >
                  <img
                    src="/assets/icons/arrow_right.png"
                    alt="Arbetsflöde"
                    className="w-4 h-4"
                  />
                  {t('Arbetsflöde')}
                </button>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-4">
                  <h1 className={`${item.name?.length > 20 ? 'text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl' : 'text-2xl mobile:text-3xl tablet:text-4xl laptop:text-4xl desktop:text-4xl'} font-bold text-gray-900`}>
                    {item.name}
                  </h1>
                  {/* {item.country_flag && (
                    <div className="inline-flex items-center bg-white/90 backdrop-blur-sm border-2 border-gray-200 rounded-full shadow-none px-3 py-1.5">
                      <img
                        src={item.country_flag}
                        alt=""
                        className="h-5 w-auto rounded-sm mr-2"
                      />
                      <span className="text-sm font-bold text-gray-800 tracking-wide">
                        {getCountryCodeFromFlag(item.country_flag)}
                      </span>
                    </div>
                  )} */}
                </div>
                {/* Action Buttons */}
                <div className="flex items-center space-x-3 mobile:hidden">
                  {/* Publish Button */}
                  <button
                    onClick={isPublished ? handleOpenConsupediaProduct : handlePublishDish}
                    disabled={publishing || !item?.gtin}
                    className={`px-4 py-2 rounded-lg font-normal text-sm transition-all duration-200 flex items-center ${
                      isPublished
                        ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-200'
                        : publishing
                        ? 'bg-blue-100 text-blue-700 border border-blue-200 cursor-not-allowed'
                        : item?.gtin
                        ? 'bg-none hover:to-blue-700 text-black  hover:shadow-sm transform hover:scale-105 border border-gray-200'
                        : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    {publishing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        {t('Publishing...')}
                      </>
                    ) : isPublished ? (
                      <>
                        <FiExternalLink className="w-4 h-4 mr-2" />
                        {t('Published')}
                      </>
                    ) : (
                      <>
                        <img src="/assets/icons/svg/publish.svg" alt="Publish" className="w-4 h-4 mr-2" />
                        {t('Publicera som produkt')}
                      </>
                    )}
                  </button>
                  
                  {/* Unpublish Button */}
                  {isPublished && (
                    <>
                      <button
                        onClick={handleUnpublishDish}
                        disabled={publishing}
                        className="px-4 py-2 rounded-lg font-normal text-sm transition-all duration-200 flex items-center bg-none text-black hover:shadow-sm transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none border border-gray-200"
                      >
                        {publishing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            {t('Unpublishing...')}
                          </>
                        ) : (
                          <>
                            <img src="/assets/icons/svg/unpublish.svg" alt="Unpublish" className="w-4 h-4 mr-2" />
                            {t('Unpublish')}
                          </>
                        )}
                      </button>
                    </>
                  )}
                  
                  {/* Recipe Print Button */}
                  <div className="mobile:hidden">
                    <DishRecipeFileToPrint dish={item} />
                  </div>
                  {/* Print Button */}
                  <div className="block mobile:hidden">
                    <DishFileToPrint
                      dish={item}
                      parameters={[]}
                      dishData={item}
                      ethic={item.ethic}
                      sessionId="menuwise"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowQrModal(true)}
                    className="px-4 py-2 rounded-lg font-normal text-sm transition-all duration-200 flex items-center bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-200"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mr-2">
                      <path
                        d="M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm10 0h2v2h-2v-2Zm-2-2h2v2h-2v-2Zm4 0h4v4h-2v-2h-2v-2Zm0 6h2v2h-2v-2Zm2-2h2v4h-4v-2h2v-2Zm-6 0h2v4h-2v-4Zm6 6h2v2h-2v-2Zm-4 0h2v2h-2v-2Z"
                        fill="currentColor"
                      />
                    </svg>
                    {t('QR-kod')}
                  </button>
                </div>
              </div>
              <p className="text-sm mobile:text-base tablet:text-lg laptop:text-lg desktop:text-lg text-gray-600">
                {t('Detaljerad information om receptet och dess ingredienser')}
              </p>
              {item.creator && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <UserCheck className="w-4 h-4 mr-2" />
                  <span>
                    Skapad av: <span>{item.creator.name}</span> •{" "}
                    {item?.created_at
                      ? new Date(item?.created_at).toLocaleDateString("sv-SE")
                      : ""}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recipe Overview Card */}
        <DishSingleViewSummeryCard
          item={item}
          onClick={() => {}}
          className="mb-8"
        />

        {/* Ingredients Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">{t('Ingredienser')}</h3>
              <div className="flex items-center space-x-4 mobile:hidden">
                {/* Save order button moved to fixed bottom bar */}
                <button
                  type="button"
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowCompositionsDrawer(true)}
                >
                  <PiBowlFoodLight className="w-6 h-6 mr-2" />
                  {t("Hälsofaktorer")}
                </button>
                <button
                  type="button"
                  className="bg-secondary hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowNutrientDrawer(true)}
                >
                  <CiViewList className="w-5 h-5 mr-2" />
                  {t('Näringsvärde')}
                </button>
                <button
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowParametersModal(true)}
                >
                  <img
                    src="/assets/icons/svg/parameter.svg"
                    alt="Parametrar"
                    className="w-6 h-6 mr-2"
                  />
                  {t('Parametrar')}
                </button>
                <button
                  onClick={() => setAdding(!adding)}
                  className={
                    adding
                      ? "bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                      : "bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  }
                >
                  {adding ? (
                    <>
                      <img
                        src="/assets/icons/Dolj.png"
                        alt="Cancel"
                        className="w-5 h-5 mr-2"
                      />
                      {t("Avbryt")}
                    </>
                  ) : (
                    <>
                      <img
                        src="/assets/icons/Plus.png"
                        alt="Add"
                        className="w-5 h-5 mr-2"
                      />
                      {t("Lägg till Ingrediens")}
                    </>
                  )}
                </button>
              </div>
            </div>
            {/* Mobile buttons under title */}
            <div className="hidden mobile:grid grid-cols-1 gap-3 mt-4">
              <DishRecipeFileToPrint dish={item} />
              <button
                type="button"
                onClick={() => setShowQrModal(true)}
                className="w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="mr-2">
                  <path
                    d="M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm10 0h2v2h-2v-2Zm-2-2h2v2h-2v-2Zm4 0h4v4h-2v-2h-2v-2Zm0 6h2v2h-2v-2Zm2-2h2v4h-4v-2h2v-2Zm-6 0h2v4h-2v-4Zm6 6h2v2h-2v-2Zm-4 0h2v2h-2v-2Z"
                    fill="currentColor"
                  />
                </svg>
                {t('QR-kod')}
              </button>
              <button
                type="button"
                className="w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowCompositionsDrawer(true)}
              >
                <img
                  src="/assets/icons/Innehall.png"
                  alt="Compositions"
                  className="w-5 h-5 mr-2"
                />
                {t("Hälsofaktorer")}
              </button>
              <button
                type="button"
                className="w-full justify-center bg-secondary hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowNutrientDrawer(true)}
              >
                <CiViewList className="w-5 h-5 mr-2" />
                {t('Näringsvärde')}
              </button>
              <button
                className="w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowParametersModal(true)}
              >
                <img
                  src="/assets/icons/Miljo.png"
                  alt="Parametrar"
                  className="w-5 h-5 mr-2"
                />
                {t('Parametrar')}
              </button>
              <button
                onClick={() => setAdding(!adding)}
                className={
                  adding
                    ? "w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                    : "w-full justify-center bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                }
              >
                {adding ? (
                  <>
                    <img
                      src="/assets/icons/Dolj.png"
                      alt="Cancel"
                      className="w-5 h-5 mr-2"
                    />
                    {t("Avbryt")}
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/icons/Plus.png"
                      alt="Add"
                      className="w-5 h-5 mr-2"
                    />
                    {t("Lägg till Ingrediens")}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Filter Panel removed */}

          {/* Ingredients Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Ingrediens")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Miljövärderingar")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Mängd (g/ml)")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Procent")}
                  </th>
                  <th className="px-2 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Inköpspris")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Ursprung/Produktion")}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Åtgärder")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredComponents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <img
                            src="/assets/icons/Innehall.png"
                            alt="No ingredients"
                            className="h-12 w-12 text-gray-400"
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {t("Inga ingredienser hittades")}
                        </h3>
                        <p className="text-gray-600 mb-8">
                          {showFilter
                            ? t("Försök att justera dina filterkriterier.")
                            : t("Inga ingredienser läggs till ännu.")}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const groups = new Map();
                    filteredComponents.forEach((c) => {
                      const key = (c.heading || "").trim();
                      if (!groups.has(key)) groups.set(key, []);
                      groups.get(key).push(c);
                    });
                    const entries = Array.from(groups.entries());
                    return entries.map(([heading, comps], gi) => (
                      <React.Fragment key={`group-${gi}`}>
                        {heading && (
                          <tr key={`group-head-${gi}`}>
                            <td
                              colSpan="6"
                              className="px-6 py-2 bg-gray-50 text-gray-700 font-semibold border-t border-gray-100"
                            >
                              {heading}
                            </td>
                          </tr>
                        )}
                        {comps.map((component) => (
                          <TableRowComponent
                            component={component}
                            dishId={id}
                            updateItem={() => dispatch(fetchDishByIdThunk(id))}
                            key={component.id}
                            draggable
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          />
                        ))}
                      </React.Fragment>
                    ));
                  })()
                )}
              </tbody>
            </table>
          </div>

          {/* Extra Ingredients Simple Display (when collapsed) */}
          {!showExtraIngredientsExpanded &&
            item.extra_ingredients &&
            item.extra_ingredients.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img
                      src="/assets/icons/Innehall.png"
                      alt="Extra ingredients"
                      className="w-4 h-4 mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t("Extra ingredienser")}:
                    </span>
                  </div>
                  <button
                    onClick={() => setShowExtraIngredientsExpanded(true)}
                    className="text-sm text-secondary-green hover:text-green-600 transition-colors"
                  >
                    {t("Hantera")}
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(item.extra_ingredients || []).map((ingredient, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-700"
                    >
                      {ingredient}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Add Extra Ingredients Button (when no extra ingredients exist) */}
          {!showExtraIngredientsExpanded &&
            (!item.extra_ingredients ||
              item.extra_ingredients.length === 0) && (
              <div className="px-6 py-3 border-t border-gray-100">
                <button
                  onClick={() => setShowExtraIngredientsExpanded(true)}
                  className="flex items-center text-sm text-secondary-green hover:text-green-600 transition-colors"
                >
                  <img
                    src="/assets/icons/Innehall.png"
                    alt="Add extra ingredients"
                    className="w-4 h-4 mr-2"
                  />
                  {t("Lägg till extra ingredienser")}
                </button>
              </div>
            )}

          {/* Extra Ingredients Section */}
          {showExtraIngredientsExpanded && (
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("Extra ingredienser")}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Lägg till ingredienser som inte finns i databasen")} (t.ex.
                    {t("Krydda efter behov")})
                  </p>
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <img
                        src="/assets/icons/FYI.png"
                        alt="Info"
                        className="w-4 h-4 mr-2 flex-shrink-0"
                      />
                      <div className="text-xs text-yellow-800">
                        <strong>{t("Obs:")}</strong> {t("Dessa ingredienser får inga hållbarhetsvärden men visas för information.")}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowExtraIngredientsExpanded(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Extra Ingredients List */}
              <div className="space-y-2 mb-4">
                {item.extra_ingredients && item.extra_ingredients.length > 0 ? (
                  (item.extra_ingredients || []).map((ingredient, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3"
                    >
                      <span className="text-sm text-gray-700">
                        {ingredient}
                      </span>
                      <button
                        onClick={() => {
                          const updatedIngredients =
                            item.extra_ingredients.filter(
                              (_, i) => i !== index
                            );
                          handleUpdateExtraIngredients(updatedIngredients);
                        }}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <img
                          src="/assets/icons/Radera.png"
                          alt="Delete"
                          className="w-4 h-4"
                        />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <img
                      src="/assets/icons/Innehall.png"
                      alt="No extra ingredients"
                      className="w-8 h-8 mx-auto mb-2 opacity-50"
                    />
                    <p className="text-sm">
                      {t("Inga extra ingredienser tillagda än")}
                    </p>
                  </div>
                )}
              </div>

              {/* Add Extra Ingredient Form */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t("Lägg till extra ingrediens (t.ex. Krydda efter behov)")}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-sm"
                  value={newExtraIngredient}
                  onChange={(e) => setNewExtraIngredient(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddExtraIngredient();
                    }
                  }}
                />
                <button
                  onClick={handleAddExtraIngredient}
                  disabled={
                    !newExtraIngredient.trim() || updatingExtraIngredients
                  }
                  className="px-4 py-2 bg-secondary-green text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {updatingExtraIngredients ? t("Lägger till...") : t("Lägg till")}
                </button>
              </div>
            </div>
          )}

          {/* Add Ingredient Form */}
          <TableRowComponentNew
            itemId={id}
            adding={adding}
            updateItem={() => dispatch(fetchDishByIdThunk(id))}
          />
          {originalContentsDisplay && (
            <div className="px-6 pb-4 text-sm text-gray-700 m-6 border-t border-gray-100 pt-6">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h4 className="text-base font-bold text-gray-900">
                    {t("Innehållsförteckning")}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="inline-flex items-center bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-2 px-3 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
                      onClick={handlePrintIngredientsSheet}
                      aria-label={t("Skriv ut ingredienslista")}
                    >
                      <PiPrinterLight className="w-4 h-4 mr-2" />
                      {t("Skriv ut")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-2 px-3 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            originalContentsDisplay
                          );
                          setCopiedIngredients(true);
                          setTimeout(() => setCopiedIngredients(false), 2000);
                        } catch (e) {
                          console.error(
                            t("Kunde inte kopiera ingredienslista"),
                            e
                          );
                        }
                      }}
                      aria-label={t("Kopiera ingredienslista")}
                    >
                      <img
                        src="/assets/icons/Check.png"
                        alt="Copy"
                        className="w-4 h-4 mr-2"
                      />
                      {copiedIngredients ? t("Kopierad!") : t("Kopiera")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-2 px-3 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
                      onClick={() => setShowIngredientsExpanded((v) => !v)}
                      aria-expanded={showIngredientsExpanded}
                      aria-controls="ingredients-original-contents"
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transform transition-transform ${
                          showIngredientsExpanded ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {showIngredientsExpanded ? t("Visa mindre") : t("Visa mer")}
                    </button>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div
                    className={`relative ${
                      showIngredientsExpanded ? "" : "max-h-40 overflow-hidden"
                    }`}
                    id="ingredients-original-contents"
                  >
                    <p className="whitespace-pre-wrap leading-relaxed text-gray-800">
                      {originalContentsDisplay}
                    </p>
                    {!showIngredientsExpanded && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-white/0 pointer-events-none" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h4 className="text-base font-bold text-gray-900">
              {t("Beskrivning av rätten")}
            </h4>
          </div>
          <div className="px-6 py-4">
            {/* Formatting Help */}
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("Formateringstips:")}
                </span>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setDescription(description + "\n• ")}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
                  >
                    {t("Lägg till punkt")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDescription(description + "\n- ")}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
                  >
                    {t("Lägg till bindestreck")}
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <div>• {t("Tryck Enter för ny rad")}</div>
                <div>
                  • {t("Om raden börjar med • eller -, läggs automatiskt till på nästa rad")}
                </div>
                <div>• {t("Använd mellanslag för att skapa struktur")}</div>
              </div>
            </div>

            <textarea
              placeholder={t("Lägg till en beskrivning av rätten...") + "\n\n" + t("Exempel:") + "\n• " + t("Färsk och saftig rätt") + "\n• " + t("Perfekt för lunch eller middag") + "\n• " + t("Innehåller lokala ingredienser") + "\n• " + t("Serveras med färsk sallad")}
              maxLength={1000}
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none font-mono text-xs"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                // Auto-add bullet points on new lines
                if (e.key === "Enter") {
                  e.preventDefault();
                  const cursorPos = e.target.selectionStart;
                  const textBefore = description.substring(0, cursorPos);
                  const textAfter = description.substring(cursorPos);

                  // Check if we're starting a new line
                  const lines = textBefore.split("\n");
                  const currentLine = lines[lines.length - 1];

                  // If current line starts with bullet, add bullet to new line
                  if (
                    currentLine.trim().startsWith("•") ||
                    currentLine.trim().startsWith("-")
                  ) {
                    const newText = textBefore + "\n• " + textAfter;
                    // Check if adding this would exceed 200 characters
                    if (newText.length <= 1000) {
                      setDescription(newText);
                      // Set cursor position after the bullet
                      setTimeout(() => {
                        e.target.setSelectionRange(
                          cursorPos + 3,
                          cursorPos + 3
                        );
                      }, 0);
                    }
                  } else {
                    // Regular new line
                    const newText = textBefore + "\n" + textAfter;
                    // Check if adding this would exceed 200 characters
                    if (newText.length <= 1000) {
                      setDescription(newText);
                      setTimeout(() => {
                        e.target.setSelectionRange(
                          cursorPos + 1,
                          cursorPos + 1
                        );
                      }, 0);
                    }
                  }
                }
              }}
            />
            <div className="mt-2 text-right">
              <span
                className={`text-xs ${
                  description.length > 950
                    ? "text-orange-500"
                    : description.length > 1000
                    ? "text-red-500"
                    : "text-gray-500"
                }`}
              >
                {description.length}/1000 {t("tecken")}
              </span>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={handleSaveDescription}
                disabled={savingDescription}
                className="inline-flex items-center bg-secondary-green hover:bg-secondary-green/90 disabled:bg-gray-400 text-white font-semibold text-xs py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
              >
                {savingDescription ? (
                  <>
                    <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                      <img
                        src="/assets/icons/Innehall.png"
                        alt="Loading"
                        className="w-full h-full"
                      />
                    </div>
                    {t("Sparar...")}
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/icons/Check.png"
                      alt="Save"
                      className="w-4 h-4 mr-2"
                    />
                    {t("Spara beskrivning")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Meal Attributes Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h4 className="text-base font-bold text-gray-900">
              {t("Måltidsinställningar")}
            </h4>
            <button
              type="button"
              onClick={handleMealAttributesUpdate}
              disabled={updatingMealAttributes}
              className="inline-flex items-center bg-secondary-green hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold text-xs py-2 px-3 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              {updatingMealAttributes ? (
                <>
                  <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                    <img
                      src="/assets/icons/Innehall.png"
                      alt="Loading"
                      className="w-full h-full"
                    />
                  </div>
                  {t("Sparar...")}
                </>
              ) : (
                <>
                  <img
                    src="/assets/icons/Check.png"
                    alt="Save"
                    className="w-4 h-4 mr-2"
                  />
                  {t("Spara")}
                </>
              )}
            </button>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-3 mobile:grid-cols-1 gap-6">
              {/* Meal Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Måltidstyper")}
                </label>
                <div className="space-y-2">
                  {[
                    { value: "frukost", label: t("Frukost") },
                    { value: "lunch", label: t("Lunch") },
                    { value: "middag", label: t("Middag") },
                    { value: "mellanmål", label: t("Mellanmål") },
                    { value: "småmål", label: t("Småmål") },
                    { value: "kvällsmål", label: t("Kvällsmål") },
                    { value: "desser", label: t("Desser") },
                    { value: "övrigt", label: t("Övrigt") },
                  ].map((type) => (
                    <label key={type.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={mealTypes.includes(type.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMealTypes([...mealTypes, type.value]);
                          } else {
                            setMealTypes(
                              mealTypes.filter((t) => t !== type.value)
                            );
                          }
                        }}
                        className="h-4 w-4 text-secondary-green focus:ring-secondary-green border-gray-300 rounded accent-secondary-green"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Day Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Veckodagar")}
                </label>
                <div className="space-y-2">
                  {[
                    { value: "monday", label: t("Måndag") },
                    { value: "tuesday", label: t("Tisdag") },
                    { value: "wednesday", label: t("Onsdag") },
                    { value: "thursday", label: t("Torsdag") },
                    { value: "friday", label: t("Fredag") },
                    { value: "saturday", label: t("Lördag") },
                    { value: "sunday", label: t("Söndag") },
                  ].map((day) => (
                    <label key={day.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dayTags.includes(day.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDayTags([...dayTags, day.value]);
                          } else {
                            setDayTags(dayTags.filter((d) => d !== day.value));
                          }
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {day.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Dagens meny")}
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setIsDagensMeny(!isDagensMeny)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      isDagensMeny ? "bg-secondary-green" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isDagensMeny ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm text-gray-600">
                    {isDagensMeny ? t("Aktiverad") : t("Inaktiverad")}
                  </span>
                </div>
              </div>
            </div>

            {/* Current Status Display */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                {t("Aktuell status:")}
              </h5>
              <div className="flex flex-wrap gap-2">
                {mealTypes.length > 0 &&
                  mealTypes.map((type) => {
                    const typeLabels = {
                      frukost: t("Frukost"),
                      lunch: t("Lunch"),
                      middag: t("Middag"),
                      mellanmål: t("Mellanmål"),
                      småmål: t("Småmål"),
                      kvällsmål: t("Kvällsmål"),
                      desser: t("Desser"),
                      övrigt: t("Övrigt"),
                    };
                    return (
                      <span
                        key={type}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {typeLabels[type] || type}
                      </span>
                    );
                  })}
                {dayTags.length > 0 &&
                  dayTags.map((day) => (
                    <span
                      key={day}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {day === "monday"
                        ? t("Måndag")
                        : day === "tuesday"
                        ? t("Tisdag")
                        : day === "wednesday"
                        ? t("Onsdag")
                        : day === "thursday"
                        ? t("Torsdag")
                        : day === "friday"
                        ? t("Fredag")
                        : day === "saturday"
                        ? t("Lördag")
                        : t("Söndag")}
                    </span>
                  ))}
                {isDagensMeny && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {t("Dagens meny")}
                  </span>
                )}
                {mealTypes.length === 0 &&
                  dayTags.length === 0 &&
                  !isDagensMeny && (
                    <span className="text-sm text-gray-500">
                      {t("Inga inställningar valda")}
                    </span>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Public info page (QR) — separate from meal settings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-8">
          <div className="px-6 py-4 border-b border-gray-100">
            <h4 className="text-base font-bold text-gray-900">
              {t('Publik rättsida')}
            </h4>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-3xl">
              {t('publicDishPageHelp', {
                defaultValue:
                  t('Låter gäster öppna en samlad vy med namn, bild, beskrivning, ingredienser, näring och hållbarhet via länk eller QR-kod utan inloggning. Använd knappen QR-kod ovan för att skriva ut. Stäng av om rätten bara ska visas i portalen.'),
              })}
            </p>
          </div>
          <div className="px-6 py-4 bg-gray-50/50">
            <label className="inline-flex items-center gap-3 cursor-pointer select-none group">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 rounded border-gray-300 text-secondary-green focus:ring-secondary-green focus:ring-offset-0"
                checked={isPublicPage}
                disabled={savingPublicPage}
                onChange={(e) => handlePublicPageToggle(e.target.checked)}
              />
              <span className="text-sm font-semibold text-gray-900 group-hover:text-gray-950">
                {t('publicDishPageToggle', {
                  defaultValue: 'Visa publik rättsida',
                })}
              </span>
            </label>
          </div>
        </div>
        {/* Drawers rendered in portal to ensure full viewport coverage */}
        {typeof document !== "undefined"
          ? createPortal(
              <>
                <SideDrawer
                  open={showNutrientDrawer}
                  onClose={() => setShowNutrientDrawer(false)}
                  title={t("Näringsvärde receptnivå")}
                  titleHint={t("Energi, protein, fett, kolhydrater, fiber, sockerarter, salt, kalium, natrium, fosfor osv.")}
                  titleIcon={
                    <CiViewList className="w-5 h-5" />
                  }
                  ariaLabel={t("Näringsvärde receptnivå")}
                >
                  {Array.isArray(item.nutrients) && item.nutrients.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Nutrient")}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Per 100g")}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Per portion")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {(() => {
                        const priority = [
                          "energi",
                          "protein",
                          "fibrer",
                          "fett",
                          "mättat fett",
                          "sockerarter",
                          "kolhydrater",
                          "salt",
                          "kalium",
                          "natrium",
                          "fosfor",
                        ];

                        // Calculate energy from final macro values using multiplying factors
                        const calculatedEnergyPer100g = calculateEnergyFromMacros(item.nutrients, false);
                        const calculatedEnergyPerPortion = calculateEnergyFromMacros(item.nutrients, true);

                        // CURRENT: Filter to only show nutrients in the priority list
                        const filtered = item.nutrients.filter((n) => {
                          const nm = (n.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          return priority.includes(nm);
                        });
                        // Sort by priority order
                        const sorted = filtered.sort((a, b) => {
                          const an = (a.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          const bn = (b.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          return priority.indexOf(an) - priority.indexOf(bn);
                        });

                        // ORIGINAL LOGIC (commented out - shows all nutrients):
                        // const byPriority = (n) => {
                        //   const nm = (n.name || "")
                        //     .toString()
                        //     .trim()
                        //     .toLowerCase();
                        //   const idx = priority.indexOf(nm);
                        //   return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
                        // };
                        // const sorted = [...item.nutrients].sort((a, b) => {
                        //   const pa = byPriority(a);
                        //   const pb = byPriority(b);
                        //   if (pa !== pb) return pa - pb;
                        //   const an = (a.name || "").toString().toLowerCase();
                        //   const bn = (b.name || "").toString().toLowerCase();
                        //   if (an < bn) return -1;
                        //   if (an > bn) return 1;
                        //   return 0;
                        // });

                        return sorted.map((n) => {
                          const nutrientName = (n.name || "").toString().trim().toLowerCase();
                          const isEnergy = nutrientName === "energi" || nutrientName === "energy" || nutrientName === "kilokalorier" || nutrientName === "kcal";
                          
                          // For energy, use macro-based kcal (same source as product materialization), then
                          // Math.ceil to match public product "Näringsvärden" table (nutrients.blade uses ceil for kcal).
                          const rawPer100g =
                            isEnergy && calculatedEnergyPer100g > 0
                              ? calculatedEnergyPer100g
                              : Number(n.amount_per_100g || 0);
                          const rawPerPortion =
                            isEnergy && calculatedEnergyPerPortion > 0
                              ? calculatedEnergyPerPortion
                              : Number(n.amount_per_portion || 0);
                          const displayAmountPer100g = isEnergy
                            ? Math.ceil(rawPer100g)
                            : rawPer100g;
                          const displayAmountPerPortion = isEnergy
                            ? Math.ceil(rawPerPortion)
                            : rawPerPortion;

                          return (
                            <tr key={`${n.nutrient_id}-${n.unit_id}`}>
                              <td className="px-4 py-2 text-sm text-gray-700">
                               {t(n.name || n.nutrient_id)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {isEnergy
                                  ? displayAmountPer100g
                                  : displayAmountPer100g.toFixed(2)}{" "}
                                {n.unit_abbr || ""}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {isEnergy
                                  ? displayAmountPerPortion
                                  : displayAmountPerPortion.toFixed(2)}{" "}
                                {n.unit_abbr || ""}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                        </table>
                      </div>
                      <DishEnergyDistributionChart nutrients={item.nutrients} t={t} />
                    </>
                  ) : (
                    <div className="text-sm text-gray-600">
                      {t("Ingen näringsinformation tillgänglig.")}
                    </div>
                  )}
                </SideDrawer>

                <SideDrawer
                  open={showCompositionsDrawer}
                  onClose={() => setShowCompositionsDrawer(false)}
                  title={t("Hälsofaktorer")}
                  titleHint={t("Fullkorn, tillsatt socker, grön andel och rött kött")}
                  titleIcon={
                    <PiBowlFoodLight className="w-5 h-5" />
                  }
                  ariaLabel={t("Hälsofaktorer")}
                >
                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                    <CompositionPercentagesCard
                      values={item?.additional_compositions}
                      portionSize={item?.portion_size}
                      t={t}
                    />
                  </div>
                </SideDrawer>
              </>,
              document.body
            )
          : null}

        {/* Modal: Parameters Groups */}
        {showParametersModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center max-h-screen">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-modern opacity-100 transition-opacity"
              onClick={() => setShowParametersModal(false)}
            />
            {/* Modal content */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden animate-[fadeIn_0.2s_ease-out]">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">
                  {item?.name}
                </h3>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 focus:outline-none"
                  onClick={() => setShowParametersModal(false)}
                  aria-label={t("Stäng")}
                >
                  <img
                    src="/assets/icons/svg/Stang.svg"
                    alt={t("Stäng")}
                    className="w-6 h-6"
                  />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-64px)]">
                <ParametersGroups dish={item} t={t} />
              </div>
            </div>
          </div>
        )}
        <DishQrModal
          open={showQrModal}
          onClose={() => setShowQrModal(false)}
          dishId={item?.id}
          publicShareToken={item?.public_share_token || ""}
          dishName={item?.name}
          dishImage={item?.image || item?.image_url || ""}
          affiliateName={
            authUser?.name ||
            item?.affiliate?.name ||
            item?.affiliate_name ||
            item?.creator?.name ||
            ""
          }
          affiliateImage={
            authUser?.image ||
            item?.affiliate?.image ||
            item?.affiliate_logo ||
            item?.creator?.image ||
            ""
          }
          showPrint
        />
      </div>

      {orderDirty &&
        (typeof document !== "undefined" ? (
          createPortal(
            <div className="fixed inset-x-0 bottom-0 z-50 px-4 py-3 mobile:px-4 tablet:px-6 laptop:px-8 desktop:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="bg-primary-dark border border-gray-200 shadow-lg rounded-xl p-3 flex items-center justify-between text-white">
                  <span className="text-sm">{t("Ordningen har ändrats.")}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center disabled:opacity-50"
                      onClick={handleSaveOrder}
                      disabled={savingOrder}
                    >
                      {savingOrder ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2">
                          <img
                            src="/assets/icons/Innehall.png"
                            alt="Loading"
                            className="w-full h-full"
                          />
                        </div>
                      ) : (
                        <img
                          src="/assets/icons/Check.png"
                          alt="Save"
                          className="w-4 h-4 mr-2"
                        />
                      )}
                      {t("Spara ändringar")}
                    </button>
                    <button
                      className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                      onClick={() => {
                        setOrderDirty(false);
                        dispatch(fetchDishByIdThunk(id));
                      }}
                      disabled={savingOrder}
                    >
                      {t("Avbryt")}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        ) : (
          <div className="fixed inset-x-0 bottom-0 z-50 px-4 py-3 mobile:px-4 tablet:px-6 laptop:px-8 desktop:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="bg-primary-dark border border-gray-200 shadow-lg rounded-xl p-3 flex items-center justify-between text-white">
                <span className="text-sm">{t("Ordningen har ändrats.")}</span>
                <div className="flex items-center space-x-2">
                  <button
                    className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center disabled:opacity-50"
                    onClick={handleSaveOrder}
                    disabled={savingOrder}
                  >
                    {savingOrder ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2">
                        <img
                          src="/assets/icons/Innehall.png"
                          alt="Loading"
                          className="w-full h-full"
                        />
                      </div>
                    ) : (
                      <img
                        src="/assets/icons/Check.png"
                        alt="Save"
                        className="w-4 h-4 mr-2"
                      />
                    )}
                    {t("Spara ändringar")}
                  </button>
                  <button
                    className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                    onClick={() => {
                      setOrderDirty(false);
                      dispatch(fetchDishByIdThunk(id));
                    }}
                    disabled={savingOrder}
                  >
                    {t("Avbryt")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      <AlertDialog
        open={alertDialog.open}
        onOpenChange={(open) => {
          if (!open) closeAlertDialog();
        }}
      >
        <AlertDialogContent
          onEscapeKeyDown={(event) => {
            if (alertDialog.mode === "confirm") event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (alertDialog.mode === "confirm") event.preventDefault();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{alertDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertDialog.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {alertDialog.mode === "confirm" ? (
              <>
                <AlertDialogCancel
                  onClick={() => {
                    alertDialog.onCancel?.();
                    closeAlertDialog();
                  }}
                >
                  {t("Avbryt")}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    alertDialog.onConfirm?.();
                    closeAlertDialog();
                  }}
                >
                  {t("Fortsätt")}
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction onClick={closeAlertDialog}>
                {t("Stäng")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <WorkflowDrawer
        open={workflowOpen}
        onClose={() => setWorkflowOpen(false)}
        footerButtons={[
          item?.category?.id
            ? {
                type: "link",
                to: `/categories/${item.category.id}`,
                label: t('Tillbaka till kategorin'),
                icon: "/assets/icons/arrow_right.png",
                onClick: () => setWorkflowOpen(false),
              }
            : null,
          {
            type: "button",
            label: t('Jag är klar'),
            onClick: () => setWorkflowOpen(false),
          },
        ].filter(Boolean)}
      />
    </div>
  );
};

export default ItemPage;
